import { styled, Typography, Skeleton, Chip } from '@mui/material';
import NumberFlow from '@number-flow/react';
import { Price as PriceType } from '@/schemas/sceneDataSchema';
import { PricingTerm } from '@/constants/pricingTypes';
import { useQuote, useShowLoader } from '@/hooks/use-quote';

function resolveSuffix(
  isMonthlyTerm: boolean,
  cardFormat: string | null | undefined,
  dueToday: number | undefined
): string {
  const format = cardFormat ?? 'onboarding';

  if (format === 'onboarding') {
    if (isMonthlyTerm) return '/mo';
    return '/6 mo';
  }

  if (format === 'endorsements') {
    if (isMonthlyTerm) return '/mo';
    if (dueToday !== undefined && dueToday < 0) return ' refund';
    return ' due';
  }

  return isMonthlyTerm ? '/mo' : '/6 mo';
}

export const Price = ({ component }: { component: PriceType }) => {
  const quote = useQuote();
  const showLoader = useShowLoader();

  if (!quote) {
    return (
      <Typography
        variant='body1'
        color='textSecondary'
      >
        Loading price...
      </Typography>
    );
  }

  const {
    initial_term_selection,
    monthly_in_cents,
    full_term_in_cents,
    due_today_monthly_in_dollars,
    due_today_full_term_in_dollars,
    price_delta_amount,
  } = quote.details;

  const isMonthlyTerm = initial_term_selection === PricingTerm.MONTHLY;
  const priceInCents = isMonthlyTerm ? monthly_in_cents : full_term_in_cents;
  const dueToday = isMonthlyTerm ? due_today_monthly_in_dollars : due_today_full_term_in_dollars;
  const cardFormat = component.card_format ?? 'onboarding';
  const isEndorsementsFullTerm = !isMonthlyTerm && cardFormat === 'endorsements';
  const suffix = resolveSuffix(isMonthlyTerm, component.card_format, dueToday);
  const displayPrice = isEndorsementsFullTerm ? Math.abs(dueToday ?? 0) : priceInCents / 100;
  const showPlaceholder = displayPrice === 0 && cardFormat === 'onboarding';

  // infer delta price - amount is increasing (positive) or decreasing (negative)
  const priceDelta = price_delta_amount ? parseFloat(price_delta_amount) : null;
  const priceDeltaDirection = priceDelta && priceDelta >= 0 ? 'increase' : 'decrease';
  const priceDeltaDisplay = priceDelta ? Math.abs(priceDelta).toFixed(2) : null;

  if (showLoader) {
    return (
      <Skeleton
        variant='rounded'
        width='100%'
        height={54}
        animation='wave'
      />
    );
  }

  return (
    <>
      <StyledPrice variant={component.variant ?? 'h1'}>
        {showPlaceholder ? (
          '$_.__'
        ) : (
          <NumberFlow
            value={displayPrice}
            format={{ style: 'currency', currency: 'USD' }}
            suffix={suffix}
            spinTiming={{
              duration: 750,
              easing: 'ease-in-out',
            }}
            opacityTiming={{
              duration: 350,
              easing: 'ease-in-out',
            }}
            willChange={true}
          />
        )}
      </StyledPrice>
      {priceDeltaDisplay && (
        <Chip
          label={`${priceDeltaDirection === 'increase' ? '+' : '-'} $${priceDeltaDisplay} premium ${priceDeltaDirection}`}
          variant='outlined'
          color='info'
          size='small'
          sx={{ alignSelf: 'start' }}
        />
      )}
    </>
  );
};

const variantsWithSmallSuffix = ['h1', 'h2', 'h3', 'h4'];

const StyledPrice = styled(Typography)<{ variant: string }>`
  ${({ variant }) =>
    variantsWithSmallSuffix.includes(variant) &&
    `
      & number-flow-react::part(suffix) {
        font-size: 0.6em;
      }
    `}
`;
